import { z } from 'zod';
import { fieldProvenanceSchema, idSchema, isoDateSchema, isoDateTimeSchema, manualOverridesSchema, } from './common.js';
import { barcodeKindSchema, dataSourceSchema, publicationFormatSchema, scanQueueStatusSchema, scanResultSchema, seriesStatusSchema, } from './enums.js';
/** Fields every record carries for provenance and override protection. */
const auditFields = {
    dataSource: dataSourceSchema,
    manualOverrides: manualOverridesSchema,
    fieldProvenance: fieldProvenanceSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
};
// ---------------------------------------------------------------- images
export const imageSchema = z.object({
    id: idSchema,
    path: z.string(),
    mimeType: z.string(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    byteSize: z.number().int().nonnegative().nullable(),
    variants: z.record(z.string(), z.string()).nullable(),
    sourceUrl: z.url().nullable(),
    attribution: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
// ------------------------------------------------------------ publishers
export const publisherSchema = z.object({
    id: idSchema,
    name: z.string(),
    country: z.string().nullable(),
    notes: z.string().nullable(),
    ...auditFields,
});
export const publisherInputSchema = z.object({
    name: z.string().trim().min(1).max(200),
    country: z.string().trim().max(2).nullish(),
    notes: z.string().trim().max(5000).nullish(),
});
// ---------------------------------------------------------------- series
export const seriesSchema = z.object({
    id: idSchema,
    publisherId: idSchema.nullable(),
    title: z.string(),
    sortTitle: z.string().nullable(),
    volume: z.string().nullable(),
    startYear: z.number().int().nullable(),
    endYear: z.number().int().nullable(),
    language: z.string().nullable(),
    format: publicationFormatSchema,
    status: seriesStatusSchema,
    /** Total number of issues expected; drives the gap view's progress bar. */
    expectedIssueCount: z.number().int().positive().nullable(),
    description: z.string().nullable(),
    notes: z.string().nullable(),
    coverImageId: idSchema.nullable(),
    ...auditFields,
});
export const seriesInputSchema = z.object({
    publisherId: idSchema.nullish(),
    title: z.string().trim().min(1).max(300),
    sortTitle: z.string().trim().max(300).nullish(),
    volume: z.string().trim().max(50).nullish(),
    startYear: z.number().int().min(1800).max(2200).nullish(),
    endYear: z.number().int().min(1800).max(2200).nullish(),
    language: z.string().trim().min(2).max(5).nullish(),
    format: publicationFormatSchema.default('issue'),
    status: seriesStatusSchema.default('unknown'),
    expectedIssueCount: z.number().int().positive().max(10000).nullish(),
    description: z.string().trim().max(20000).nullish(),
    notes: z.string().trim().max(5000).nullish(),
    coverImageId: idSchema.nullish(),
});
// ---------------------------------------------------------------- issues
export const issueSchema = z.object({
    id: idSchema,
    seriesId: idSchema,
    /** Raw number as printed: "0", "1/2", "Annual 1", "12A". */
    number: z.string(),
    numberSort: z.number().nullable(),
    numberRank: z.number().int(),
    title: z.string().nullable(),
    variantName: z.string().nullable(),
    printing: z.number().int().positive().nullable(),
    releaseDate: isoDateSchema.nullable(),
    coverDate: isoDateSchema.nullable(),
    price: z.number().nullable(),
    currency: z.string().nullable(),
    pageCount: z.number().int().positive().nullable(),
    description: z.string().nullable(),
    coverImageId: idSchema.nullable(),
    ...auditFields,
});
export const issueInputSchema = z.object({
    seriesId: idSchema,
    number: z.string().trim().min(1).max(50),
    title: z.string().trim().max(300).nullish(),
    variantName: z.string().trim().max(150).nullish(),
    printing: z.number().int().positive().max(100).nullish(),
    releaseDate: isoDateSchema.nullish(),
    coverDate: isoDateSchema.nullish(),
    price: z.number().nonnegative().max(100000).nullish(),
    currency: z.string().trim().length(3).nullish(),
    pageCount: z.number().int().positive().max(5000).nullish(),
    description: z.string().trim().max(20000).nullish(),
    coverImageId: idSchema.nullish(),
});
// -------------------------------------------------------------- barcodes
export const barcodeSchema = z.object({
    id: idSchema,
    issueId: idSchema,
    /** Exactly what the scanner delivered. */
    raw: z.string(),
    kind: barcodeKindSchema,
    /** Digits only, check digit validated. */
    normalized: z.string(),
    /** EAN-2 or EAN-5 add-on, if the scan captured one. */
    addon: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
// ------------------------------------------------------- collection items
export const collectionItemSchema = z.object({
    id: idSchema,
    issueId: idSchema,
    quantity: z.number().int().positive(),
    condition: z.string().nullable(),
    purchasePrice: z.number().nullable(),
    purchaseCurrency: z.string().nullable(),
    purchaseDate: isoDateSchema.nullable(),
    /** Free-form physical location, e.g. "Longbox 3". */
    location: z.string().nullable(),
    isRead: z.boolean(),
    notes: z.string().nullable(),
    manualOverrides: manualOverridesSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const collectionItemInputSchema = z.object({
    issueId: idSchema,
    quantity: z.number().int().positive().max(999).default(1),
    condition: z.string().trim().max(50).nullish(),
    purchasePrice: z.number().nonnegative().max(1000000).nullish(),
    purchaseCurrency: z.string().trim().length(3).nullish(),
    purchaseDate: isoDateSchema.nullish(),
    location: z.string().trim().max(120).nullish(),
    isRead: z.boolean().default(false),
    notes: z.string().trim().max(5000).nullish(),
});
// --------------------------------------------------------------- wishlist
export const wishlistItemSchema = z.object({
    id: idSchema,
    issueId: idSchema.nullable(),
    seriesId: idSchema.nullable(),
    priority: z.number().int().min(1).max(3),
    notes: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const wishlistItemInputSchema = z
    .object({
    issueId: idSchema.nullish(),
    seriesId: idSchema.nullish(),
    priority: z.number().int().min(1).max(3).default(2),
    notes: z.string().trim().max(2000).nullish(),
})
    .refine((value) => Boolean(value.issueId) !== Boolean(value.seriesId), {
    message: 'Provide either issueId or seriesId, not both',
});
// ------------------------------------------------------------ scan models
export const scanLogSchema = z.object({
    id: idSchema,
    code: z.string(),
    addon: z.string().nullable(),
    kind: barcodeKindSchema,
    result: scanResultSchema,
    matchedIssueId: idSchema.nullable(),
    source: dataSourceSchema.nullable(),
    scannedAt: isoDateTimeSchema,
});
export const scanQueueItemSchema = z.object({
    id: idSchema,
    code: z.string(),
    addon: z.string().nullable(),
    kind: barcodeKindSchema,
    status: scanQueueStatusSchema,
    attempts: z.number().int().nonnegative(),
    lastError: z.string().nullable(),
    note: z.string().nullable(),
    resolvedIssueId: idSchema.nullable(),
    createdAt: isoDateTimeSchema,
    resolvedAt: isoDateTimeSchema.nullable(),
});
//# sourceMappingURL=entities.js.map